// Gantt geometry (spec F9). Pure: dates are program calendar dates (YYYY-MM-DD), positions are
// pixels from the left edge of the timeline. The today line uses the program day from
// packages/shared, so it moves at 00:00 America/Los_Angeles for everyone (spec §8.10).
import { addDays, today } from '@cis/shared';

export const ZOOMS = ['week', 'month', 'quarter'] as const;
export type Zoom = (typeof ZOOMS)[number];

/** Pixels per day at each zoom level. */
export const PX_PER_DAY: Readonly<Record<Zoom, number>> = { week: 24, month: 6, quarter: 2 };

const DAY_MS = 86_400_000;
const DAY_PAD = 7;
const ISO_DATE_LENGTH = 10;

export function isZoom(value: unknown): value is Zoom {
  return typeof value === 'string' && (ZOOMS as readonly string[]).includes(value);
}

/** Whole days from `from` to `day` (negative when `day` is earlier). */
export function daysBetween(from: string, day: string): number {
  return Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

export interface Span {
  readonly from: string;
  readonly to: string;
}

/** The drawn span: the data range padded by a week each side, and always holding today. */
export function chartSpan(range: Span | null, day: string): Span {
  const from = range === null || day < range.from ? day : range.from;
  const to = range === null || day > range.to ? day : range.to;
  return { from: addDays(from, -DAY_PAD), to: addDays(to, DAY_PAD) };
}

export interface Box {
  readonly left: number;
  readonly width: number;
}

/** Left and width of a bar from start to end inclusive; null without both dates. */
export function barBox(
  start: string | null,
  end: string | null,
  from: string,
  pxPerDay: number,
): Box | null {
  if (start === null || end === null) return null;
  const first = start <= end ? start : end;
  const last = start <= end ? end : start;
  return {
    left: daysBetween(from, first) * pxPerDay,
    width: (daysBetween(first, last) + 1) * pxPerDay,
  };
}

/** Where the today line goes: the middle of today's column, or null outside the span. */
export function todayLineX(span: Span, pxPerDay: number, now: Date = new Date()): number | null {
  const day = today(now);
  if (day < span.from || day > span.to) return null;
  return (daysBetween(span.from, day) + 0.5) * pxPerDay;
}

export interface Tick {
  readonly day: string;
  readonly left: number;
  readonly label: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** One tick at the first of every month in the span, labelled "Nov 2026". */
export function monthTicks(span: Span, pxPerDay: number): Tick[] {
  const start = new Date(`${span.from}T00:00:00Z`);
  const months = (daysBetween(span.from, span.to) / 28) | 0;
  return Array.from({ length: months + 2 }, (_, i) => {
    const d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + i, 1));
    const day = d.toISOString().slice(0, ISO_DATE_LENGTH);
    const label = `${MONTHS[d.getUTCMonth()] ?? ''} ${String(d.getUTCFullYear())}`;
    return { day, left: daysBetween(span.from, day) * pxPerDay, label };
  }).filter((t) => t.day >= span.from && t.day <= span.to);
}

/** Total width of the timeline in pixels. */
export function spanWidth(span: Span, pxPerDay: number): number {
  return (daysBetween(span.from, span.to) + 1) * pxPerDay;
}
